import { useEffect, useCallback } from "react";
import { useResponsiveStore } from "@/stores/editor/responsive-store";

/**
 * Hook to inject generated CSS into the document head for real-time preview
 */
export const useRealtimeCSS = () => {
  const { generatedCSS } = useResponsiveStore();

  useEffect(() => {
    // Create or update the style element
    let styleElement = document.getElementById(
      "terramatrix-realtime-css",
    ) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = "terramatrix-realtime-css";
      styleElement.type = "text/css";
      document.head.appendChild(styleElement);
    }

    // Update the CSS content
    styleElement.innerHTML = generatedCSS;

    return () => {
      // Cleanup function (optional - you might want to keep the styles)
      // const element = document.getElementById('terramatrix-realtime-css');
      // if (element) {
      //   element.remove();
      // }
    };
  }, [generatedCSS]);
};

/**
 * Hook to subscribe to responsive store changes and trigger CSS generation
 */
export const useResponsiveCSSTrigger = () => {
  const { generateCSS, responsiveProperties, availableViewports } =
    useResponsiveStore();

  // Subscribe to property changes and regenerate CSS
  useEffect(() => {
    // Trigger initial CSS generation
    generateCSS();
  }, [generateCSS]);

  // Debounced regeneration when properties change
  useEffect(() => {
    const timer = setTimeout(() => {
      generateCSS();
    }, 100); // 100ms debounce for better performance

    return () => clearTimeout(timer);
  }, [responsiveProperties, availableViewports, generateCSS]);
};

/**
 * Hook for performance-optimized CSS generation with batching
 */
export const useOptimizedCSS = () => {
  const { generateCSS, generatedCSS } = useResponsiveStore();

  // Batch CSS updates for better performance
  const batchedGenerateCSS = useCallback(() => {
    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      generateCSS();
    });
  }, [generateCSS]);

  return {
    generateCSS: batchedGenerateCSS,
    css: generatedCSS,
  };
};
